import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

// Components
import BaseTemplate from "./BaseTemplate";
import ImageButton from "new_components/molecules/ImageButton";
import SubMenu from "new_components/organisms/SubMenu";
import ContentSection from "new_components/organisms/SubScrollingContent";

// Constants
import colors from "styles/colors";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";

// Wrapper for the action row
const ActionRow = styled.div`
  display: ${({ show }) => (show ? "flex" : "none")};
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  position: fixed;
  top: ${({ headingSpace, otpStyle }) => (otpStyle ? '94px' : headingSpace ? '84px' : '64px')};
  left: ${({ isNavOpen, otpStyle }) => {
    if (otpStyle) {
      return isNavOpen ? '50px' : '50px';
    }
    return isNavOpen ? '242px' : '0';
  }};
  right: 0;
  z-index: 25;
  background-color: ${colors.background.white};
  padding: 16px 24px 0 ${({ otpStyle }) => otpStyle ? '30px' : '0'};
  transition: left 0.3s ease;
`;

// Full Page Wrapper
const LayoutWrapper = styled.div`
  display: flex;
  min-height: 100vh;
  width: 100%;
`;

// Sidebar (SubMenu) Wrapper
const SidebarWrapper = styled.div`
  flex: 0 0 260px;
  position: fixed;
  left: ${({ isNavOpen, otpStyle }) => {
    if (otpStyle) {
      return isNavOpen ? '50px' : '50px';
    }
    return isNavOpen ? '242px' : '80px';
  }};
  margin-left: ${({ otpStyle }) => otpStyle ? '0px' : '0'};
  top: ${({ headingSpace, otpStyle }) => {
    if (otpStyle) return headingSpace ? '150px' : '140px';
    return headingSpace ? '150px' : '120px';
  }};
  height: ${({ headingSpace, otpStyle }) => {
    if (otpStyle) return headingSpace ? 'calc(100vh - 170px)' : 'calc(100vh - 140px)';
    return headingSpace ? 'calc(100vh - 150px)' : 'calc(100vh - 120px)';
  }};
  
  overflow-y: auto;
  background-color: ${colors.background.white};
  padding: 16px;
  z-index: 5;
  transition: left 0.3s ease;

  /* Custom Scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.neutral[200]};
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.neutral[500]};
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.neutral[600]};
  }
`;

// Page Title
const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: ${colors.text.dark};
  margin-left: ${({ otpStyle }) => otpStyle ? '50px' : '0'};
`;

// Separator (Vertical Line)
const Separator = styled.div`
  width: 1px;
  background-color: ${colors.neutral[500]};
  position: fixed;
  left: ${({ isNavOpen, otpStyle }) => {
    if (otpStyle) {
      return isNavOpen ? '400px' : '350px';
    }
    return isNavOpen ? '502px' : '340px';
  }};
  top: ${({ otpStyle }) => otpStyle ? '140px' : '120px'};
  height: ${({ otpStyle }) => otpStyle ? 'calc(100vh - 140px)' : 'calc(100vh - 120px)'};
  z-index: 15;
  transition: left 0.3s ease;
`;

// Content Area Wrapper
const ContentWrapper = styled.div`
  flex: 1;
  padding: 24px;
  width: 100%;
  margin-left: ${({ otpStyle, isNavOpen }) => 
    otpStyle 
      ? 'calc(340px)'
      : 'calc(260px + max(0px, (1200px - 30vw) * 0.1))'};
  margin-top: ${({ otpStyle, headingSpace }) => 
    otpStyle 
      ? (headingSpace ? '100px' : '80px')
      : '130px'};
  max-width: 800px;
  transition: margin-left 0.3s ease;
`;



// Main Layout Component
const ActionMenuLayout = ({
  backAction,
  backButtonText = "Back",
  actionRowComponent,
  menuItems,
  heading,
  pageTitle,
  subTitle,
  searchable,
  type = "multi", // Default to "multi" if not specified
  onItemSelect,
  loadingComponent,
  loading = false,
  activeItem = null,
  menuNavigationRules,
  wizardMode = false,
  completedSteps = [],
  isNavOpen = true,
  topNav = false,
  otpStyle = false,
}) => {
  const contentWrapperRef = useRef(null);

  // State to manage active menu item when type is "single"
  const [activeItemId, setActiveItemId] = useState(
    activeItem || menuItems[0]?.id || menuItems[0]?.items[0]?.id
  );

  const handleMenuItemClick = (id) => {
    setActiveItemId(id);

    if (type === "single" && contentWrapperRef.current) {
      contentWrapperRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }

    // Trigger the onItemSelect callback with the newly selected item
    if (onItemSelect) {
      let selectedItem = menuItems.find((item) => item.id === id);

      if (!selectedItem) {
        // Loop through grouped menu items to find the selected item
        menuItems.forEach((group) => {
          if (group.items) {
            selectedItem = group.items.find((item) => item.id === id);
          }
        });
      }

      if (selectedItem) {
        onItemSelect(selectedItem);
      }
    }
  };

  useEffect(() => {
    if (activeItem) {
      setActiveItemId(activeItem);
    }
  }, [activeItem]);

  // Enhance menu items with completion status if wizard mode is enabled
  const enhancedMenuItems = React.useMemo(() => {
    if (!wizardMode) return menuItems;

    return menuItems.map(item => ({
      ...item,
      isCompleted: completedSteps?.includes(item.id),
    }));
  }, [menuItems, wizardMode, completedSteps]);

  return (
    <BaseTemplate
      heading={heading}
      headingSpace={heading?.length > 0}
      pageTitle={pageTitle}
      subTitle={subTitle}
      isSubContent={true}
      isNavOpen={isNavOpen}
      topNav={topNav}
      otpStyle={otpStyle}
    >
      {/* Action Row */}
      <ActionRow headingSpace={heading?.length > 0} show={backAction || actionRowComponent} isNavOpen={isNavOpen} otpStyle={otpStyle}>
        {backAction && (
          <ImageButton
            text={backButtonText}
            textStyle={true}
            icon={ICON_SET.chevronLeft}
            onClick={backAction}
            color={colors.text.gray}
          />
        )}
        {actionRowComponent}
      </ActionRow>

      {/* Main Layout */}
      <LayoutWrapper headingSpace={heading?.length > 0} topNav={topNav} otpStyle={otpStyle}>
        {/* Sidebar Menu */}
        <SidebarWrapper headingSpace={heading?.length > 0} isNavOpen={isNavOpen} otpStyle={otpStyle}>
          <SubMenu
            categories={enhancedMenuItems.map((item) => ({
              ...item,
              disabled: menuNavigationRules ? !menuNavigationRules[item.id] : false,
            }))}
            contentRef={contentWrapperRef}
            searchable={searchable}
            type={type}
            activeItemId={activeItemId}
            wizardMode={wizardMode}
            onMenuItemClick={(item) => {
              if (menuNavigationRules && !menuNavigationRules[item]) return;
              handleMenuItemClick(item);
            }}
          />
        </SidebarWrapper>
        <Separator isNavOpen={isNavOpen} otpStyle={otpStyle} />

        {/* Content Area */}
        <ContentWrapper ref={contentWrapperRef} isNavOpen={isNavOpen} otpStyle={otpStyle}>
          {!loading && (
            <ContentSection
              categories={menuItems}
              type={type}
              activeItemId={activeItemId}
            />
          )}
          {loading && loadingComponent && loadingComponent}
        </ContentWrapper>
      </LayoutWrapper>
    </BaseTemplate>
  );
};

ActionMenuLayout.propTypes = {
  backAction: PropTypes.func, // Function for back button action
  backButtonText: PropTypes.string, // Customizable text for back button
  actionRowComponent: PropTypes.node, // Custom component in the action row
  menuItems: PropTypes.arrayOf(
    PropTypes.oneOfType([
      // For flat list of categories
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        categoryTitle: PropTypes.string.isRequired,
        categoryContent: PropTypes.node.isRequired,
        categoryIcon: PropTypes.string,
        isCompleted: PropTypes.bool,
      }),
      // For grouped categories
      PropTypes.shape({
        groupTitle: PropTypes.string.isRequired,
        items: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            categoryTitle: PropTypes.string.isRequired,
            categoryContent: PropTypes.node.isRequired,
            categoryIcon: PropTypes.string,
            isCompleted: PropTypes.bool,
          })
        ).isRequired,
      }),
    ])
  ).isRequired, // Menu items for the middle menu
  heading: PropTypes.string, // Heading text
  pageTitle: PropTypes.string, // Page title
  subTitle: PropTypes.string, // Subtitle
  searchable: PropTypes.bool, // Whether the menu is searchable
  type: PropTypes.oneOf(["multi", "single"]), // Layout type
  menuNavigationRules: PropTypes.shape({
    [PropTypes.string]: PropTypes.bool, // e.g., { 'step1': true, 'step2': false }
  }),
  wizardMode: PropTypes.bool, // Enable wizard mode with step indicators
  completedSteps: PropTypes.arrayOf(PropTypes.string), // Array of completed step IDs
  isNavOpen: PropTypes.bool, // Whether the sidebar is open
};

export default ActionMenuLayout;
